import { HasAccess } from "@permify/react-role";
import {
  Badge,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Spin,
  Tabs,
  message,
} from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import * as _ from 'lodash';
import moment from 'moment';
import 'moment/locale/es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { handlers } from '../../helpers/helpers';
import { getCookie, listenerCookie, setCookie } from '../../services/cookies';
import { getReservationServices } from '../../services/reservation';
import countryRestrictions from '../../utils/countryRestrictions.json';
import countrySettings from './countrySettings.json';
import ConfirmedReservationByDate from './components/ConfirmedReservationByDate';
import DraftReservation from './components/DraftReservation';
import MassiveCharge from './components/MassiveCharge';
import useDepartments from './hooks/useDepartments';
import useReservations from './hooks/useReservations';
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';

const ReservationByDatePage = () => {
  const [reservationServices, setReservationServices] = useState([]);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const navigate = useNavigate();
  const queryString = {
    ...Object.fromEntries(urlSearchParams.entries()),
  };
  const draftReservations = useReservations('draft');
  const confirmedReservations = useReservations('confirmed');
  const allDepartments = useDepartments();
  const [visibleDatePicker, setVisibleDatePicker] = useState(false);
  const [form] = Form.useForm();
  const location = useLocation();
  const { date } = useParams();
  const { t } = useTranslation();
  const countryCode = getCookie('country_code');
  const useGranularSearchFilters = countryCode && countrySettings?.useGranularSearchFilters?.[countryCode];
  const roles = getCookie('roles') || [];
  const hasSystemWideAccess = roles.includes('direccion_medica');

  useEffect(() => {
    listenerCookie((data) => {
      if (data.name === 'branch_office_id') {
        updateQueryString({ 'branch_office_id': data.value });
      }
    })

    if (!getCookie('department_id')) {
      setCookie('department_id', 1, { path: '/' });
    }

    const values = {
      ...queryString,
      date: date
        ? moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
      currentTab: queryString.currentTab || 'drafted',
      branch_office_id: (queryString.allBranchOffices === 'true' || queryString.allBranchOffices === true) ? undefined : (queryString.branch_office_id || getCookie('branch_office_id') || 1),
      department_id: queryString.department_id || getCookie('department_id') || 1
    }

    form.setFieldsValue(values)
    const urlValues = { ...values };
    delete urlValues.date;
    handlers.replaceUrlQueryString(navigate, urlValues);
  }, [location.search, date]);

  const handleTab = (activeKey) => {
    updateQueryString({ 'currentTab': activeKey });
  }

  const updateQueryString = (update) => {
    const values = form.getFieldsValue();
    handlers.removeEmpties(values);
    
    const urlValues = {
      ...queryString,
      ...values,
      ...update
    };
    delete urlValues.date;

    handlers.replaceUrlQueryString(navigate, urlValues);
  }

  const handleSelectDepartment = (event) => {
    setCookie('department_id', event.target.value, { path: '/' });
    updateQueryString({ 'department_id': event.target.value });
    draftReservations.changePage(1);
    confirmedReservations.changePage(1);
  };

  const handleDatePicker = (date) => {
    const pickedDate = moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
    navigate({ pathname: `/reservas/fecha/${pickedDate}`, search: location.search });
  };

  const handleDayBefore = () => {
    const dayBefore = moment(form.getFieldValue('date'), 'YYYY-MM-DD').subtract(1, 'day');
    navigate({ pathname: `/reservas/fecha/${dayBefore.format('DD-MM-YYYY')}`, search: location.search });
  };

  const handleNextDay = () => {
    const nextDay = moment(form.getFieldValue('date'), 'YYYY-MM-DD').add(1, 'day');
    navigate({ pathname: `/reservas/fecha/${nextDay.format('DD-MM-YYYY')}`, search: location.search });
  };

  const formatDateChars = (date) => {
    const dateFormat = moment(date, 'YYYY-MM-DD').locale('es').format('dddd, DD [de] MMMM [de] YYYY');
    const splitDate = dateFormat.split(",");
    const lowerCaseDayName = splitDate[0].toLowerCase();
    const firstLetter = lowerCaseDayName.slice(0, 1);
    const restantText = lowerCaseDayName.slice(1);
    return firstLetter.toUpperCase() + restantText + splitDate[1];
  };

  const showDatePicker = () => {
    if (visibleDatePicker) {
      setVisibleDatePicker(false);
    } else {
      setVisibleDatePicker(true);
    }
  };

  const onFinish = (values) => {
    handlers.removeEmpties(values);
    let paramsToUpdate = {
      currentTab: form.getFieldValue('currentTab') || queryString.currentTab || 'drafted',
      department_id: form.getFieldValue('department_id'),
      date: form.getFieldValue('date') || queryString.date,
    };
    if (useGranularSearchFilters) {
      paramsToUpdate = { ...paramsToUpdate, ...values };
    } else {
      paramsToUpdate.branch_office_id = form.getFieldValue('branch_office_id');
      if (values.query) {
        paramsToUpdate.query = values.query;
      }
    }

    handlers.removeEmpties(paramsToUpdate);
    handlers.replaceUrlQueryString(navigate, paramsToUpdate);
    draftReservations.changePage(1);
    confirmedReservations.changePage(1);
  };

  const removeFilters = () => {
    if (!useGranularSearchFilters) {
      form.resetFields(['query']);
    }
    handlers.replaceUrlQueryString(navigate, {
      currentTab: form.getFieldValue('currentTab') || queryString.currentTab || 'drafted',
      branch_office_id: form.getFieldValue('branch_office_id'),
      department_id: form.getFieldValue('department_id'),
      date: form.getFieldValue('date') || queryString.date,
    })
    draftReservations.changePage(1);
    confirmedReservations.changePage(1);
  };

  useEffect(() => {
    const reservationParams = {
      ...queryString,
      department_id: form.getFieldValue('department_id'),
      branch_office_id: form.getFieldValue('branch_office_id'),
      query: form.getFieldValue('query') || queryString.query,
      date: form.getFieldValue('date'),
      outsourcing_id: form.getFieldValue('outsourcing_id') || queryString.outsourcing_id,
      user_id: form.getFieldValue('user_id') || queryString.user_id,
      allBranchOffices: form.getFieldValue('allBranchOffices') || queryString.allBranchOffices,
    }
    
    delete reservationParams.outsourcing_id;

    handlers.removeEmpties(reservationParams);

    draftReservations.setParams({
      ...reservationParams,
      type: 'drafted',
      from: 'byDate'
    });
    confirmedReservations.setParams({
      ...reservationParams,
      type: 'confirmed',
      from: 'byDate'
    });
  }, [
    location.search,
    location.pathname
  ]);

  const fetchReservationServices = async (reservationId, setInnerLoading) => {
    setInnerLoading(true);
    try {
      const response = await getReservationServices({ id: reservationId });
      const orderByBlock = _.orderBy(response, 'block');
      setReservationServices(orderByBlock);
    } catch (error) {
      console.error(error);
      message.error({ content: `Error interno ${error}`, style: { marginTop: '32vh' } });
    } finally {
      setInnerLoading(false);
    }
  };

  return (
    <Spin className="cmt-spin" spinning={draftReservations.isLoading || confirmedReservations.isLoading}>
      <Row justify="center" className="reservation-date-wrapper">
        <Col lg={24} xs={24}>
          <Row justify="center" className="reservation-date-wrapper__header mt-20">
            <Col lg={22} xs={22}>
              <Row justify="space-between" align="middle">
                <Col lg={6} xs={24}>
                  <h1 className="mb-0">{t('reservations.byDate.title')}</h1>
                  <Spin spinning={allDepartments.isLoading}>
                    {
                      form.getFieldValue('department_id')
                      && <Radio.Group
                        onChange={handleSelectDepartment}
                        defaultValue={form.getFieldValue('department_id')}
                        className="cmt-radio"
                      >
                        {
                          allDepartments?.data?.map((department, index) => (
                            <Radio.Button key={index} value={department.id}>
                              {department.name}
                            </Radio.Button>
                          ))
                        }
                      </Radio.Group>
                    }
                  </Spin>
                </Col>
                <Col lg={18} xs={24}>
                  <Row className="mt-10" justify="end" gutter={[16, 16]}>
                    <Col xxl={6} xl={7} lg={9} md={12} sm={24} xs={24}>
                      <Link to="/reservas/buscar" style={{ width: '100%' }}>
                        <HasAccess
                          roles={['super_usuario', 'branch_office_manager', 'administracion', 'direccion_medica']}
                          permissions='reservation-search'
                        >
                          <Button className="cmt-button primary outline" type="primary" size="large" block>
                            <i className="icon-system-search mr-10" />
                            {t('reservations.byDate.searchButton')}
                          </Button>
                        </HasAccess>
                      </Link>
                    </Col>
                    <Col xxl={4} xl={5} lg={5} md={12} sm={24} xs={24}>
                      <Link
                        to={{
                          pathname: `/reservas/crear`,
                          state: {
                            from: location.pathname,
                            search: location.search
                          }
                        }}
                        style={{ width: '100%' }}
                      >
                        <HasAccess
                          roles={['super_usuario', 'branch_office_manager', 'administracion']}
                          permissions='reservation-create'
                        >
                          <Button className="cmt-button primary" type="primary" size="large" block>
                            <i className="icon-system-plus mr-15" />
                            {t('reservations.byDate.newReservationButton')}
                          </Button>
                        </HasAccess>
                      </Link>
                    </Col>
                    {
                      (countryCode && countrySettings?.massiveCharge?.[countryCode] && [31, 21, 40, 53, 25].includes(Number(form.getFieldValue('branch_office_id')))) &&
                      <Col xxl={4} xl={5} lg={5} md={12} sm={24} xs={24}>
                        <HasAccess
                          roles={['super_usuario', 'branch_office_manager', 'administracion']}
                          permissions='reservation-create'
                        >
                          <MassiveCharge />
                        </HasAccess>
                      </Col>
                    }

                    {
                      (
                        countryCode && countryRestrictions?.[countryCode]
                        && !countryRestrictions[countryCode].find(item => item === 'reevaluation')
                      )
                      && <Col xxl={4} xl={5} lg={5} md={12} sm={24} xs={24}>
                        <Link
                          to="/reevaluaciones/buscar"
                          style={{ width: '100%' }}
                        >
                          <HasAccess
                            roles={['super_usuario', 'branch_office_manager', 'administracion']}
                            permissions='reservation-reevaluate'
                          >
                            <Button
                              className='cmt-button secondary'
                              type='primary'
                              size='large'
                              block
                            >
                              {t('reservations.byDate.reevaluationsButton')}
                            </Button>
                          </HasAccess>
                        </Link>
                      </Col>
                    }
                  </Row>
                </Col>
              </Row>
            </Col>
            <Col lg={24} xs={24} className="date-container mt-15 pt-20 pb-20">
              <Row justify="center">
                <Col
                  xl={{ order: 1, span: 3 }} lg={{ order: 1, span: 4 }} xs={{ order: 2, span: 10 }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <button className="chevron-button" onClick={handleDayBefore}>
                    <i className="icon-system-chevron-left mr-5" />
                    {t('reservations.byDate.dayBeforeButton')}
                  </button>
                </Col>
                <Col
                  xxl={{ order: 2, span: 6 }} xl={{ order: 2, span: 7 }} lg={{ order: 2, span: 8 }} xs={{ order: 1, span: 23 }}
                  style={{ display: 'flex', justifyContent: 'center' }}
                  className="mb-lg-0 mb-md-20 mb-sm-20 mb-xs-20"
                >
                  <p className="text-center mb-0" onClick={showDatePicker}>
                    {formatDateChars(form.getFieldValue('date'))}
                  </p>
                  <DatePicker
                    className="date-picker"
                    popupClassName="custom-dropdown position-fix"
                    placeholder={''}
                    inputReadOnly={true}
                    bordered={false}
                    locale={locale}
                    format={formatDateChars}
                    onChange={handleDatePicker}
                    open={visibleDatePicker}
                    onOpenChange={showDatePicker}
                  />
                </Col>
                <Col
                  xl={{ order: 3, span: 3 }}
                  lg={{ order: 3, span: 4 }}
                  xs={{ order: 3, span: 10 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <button className="chevron-button" onClick={handleNextDay}>
                    {t('reservations.byDate.nextDayButton')}
                    <i className="icon-system-chevron-right ml-5" />
                  </button>
                </Col>
              </Row>
            </Col>
            <Col lg={22} xs={22} className="mt-20">
              {useGranularSearchFilters ? (
                <UnifiedSearchFilters
                  moduleName={t('reservations.byDate.title')}
                  baseFilters={['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'evaluationType', 'allBranchOffices']}
                  customFilters={['wayContactForm', 'applicant', 'costCenter', 'timeBlock']}
                  isCollapsible={true}
                  userHasSystemWideAccess={hasSystemWideAccess}
                  onSearch={onFinish}
                  onClear={removeFilters}
                  loading={draftReservations.isLoading || confirmedReservations.isLoading}
                  initialValues={{
                    ...queryString,
                    company_id: queryString.company_id ? parseInt(queryString.company_id) : undefined,
                    outsourcing_id: queryString.outsourcing_id ? parseInt(queryString.outsourcing_id) : undefined,
                    third_company_id: queryString.third_company_id ? parseInt(queryString.third_company_id) : undefined,
                    branch_office_id: (queryString.allBranchOffices === 'true' || queryString.allBranchOffices === true) ? undefined : (queryString.branch_office_id ? parseInt(queryString.branch_office_id) : parseInt(getCookie('branch_office_id'))),
                    allBranchOffices: queryString.allBranchOffices === 'true' || queryString.allBranchOffices === true,
                    protocol_id: queryString.protocol_id ? parseInt(queryString.protocol_id) : undefined,
                    cost_center_id: queryString.cost_center_id ? parseInt(queryString.cost_center_id) : undefined,
                    block_id: queryString.block_id ? parseInt(queryString.block_id) : undefined,
                    user_id: queryString.user_id ? parseInt(queryString.user_id) : undefined,
                    evaluation_types: queryString.evaluation_types ? (Array.isArray(queryString.evaluation_types) ? queryString.evaluation_types.map(v => String(v)) : [String(queryString.evaluation_types)]) : undefined,
                  }}
                />
              ) : (
                <Form form={form} onFinish={onFinish} size="large">
                  <Form.Item name="currentTab" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="department_id" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="branch_office_id" hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item name="date" hidden>
                    <Input />
                  </Form.Item>
                  <Row>
                    <Col xxl={7} xl={8} lg={9} xs={24} className="mr-lg-15">
                      <Form.Item name="query">
                        <Input
                          className="cmt-input"
                          placeholder={t('reservations.byDate.queryPlaceholder')}
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={2} xs={8} className="mr-15">
                      <Button htmlType="submit" className="cmt-button primary" type="primary">
                        Filtrar
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        className="cmt-button primary outline borderless"
                        onClick={removeFilters}
                      >
                        Borrar filtro
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}
            </Col>
          </Row>
          <Row justify="center" className="reservation-date-wrapper__body mt-20">
            <Col lg={24} xs={24}>
              {
                form.getFieldValue('currentTab') &&
                <Tabs
                  className="cmt-tabs"
                  defaultActiveKey={form.getFieldValue('currentTab')}
                  onChange={handleTab}
                >
                  <items
                    key="drafted"
                    tab={
                      <Badge
                        count={
                          draftReservations?.pagination?.total || 0
                        }
                        offset={[15, 0]}
                        showZero={true}
                      >
                        <p className="mb-0">{t('reservations.byDate.title')} en borrador</p>
                      </Badge>
                    }
                  >
                    {
                      (draftReservations.isLoading && !draftReservations.data && !draftReservations.pagination) ?
                        <Row justify="center">
                          <Spin className="custom-spinner" />
                        </Row>
                        :
                        <DraftReservation
                          draftReservations={draftReservations}
                          fetchReservationServices={fetchReservationServices}
                          reservationServices={reservationServices}
                          draftByDate={true}
                        />
                    }
                  </items>
                  <items
                    key="confirmed"
                    tab={
                      <Badge count={confirmedReservations.pagination ? confirmedReservations.pagination.total : 0} offset={[15, 0]} showZero={true}>
                        <p className="mb-0">{t('reservations.byDate.title')} confirmadas</p>
                      </Badge>
                    }
                  >
                    {
                      confirmedReservations.isLoading
                        ?
                        <Row justify="center">
                          <Spin className="custom-spinner" />
                        </Row>
                        :
                        <ConfirmedReservationByDate
                          selectedDepartment={form.getFieldValue('department_id')}
                          confirmedReservations={confirmedReservations}
                          queryParams={{
                            ...queryString,
                            department_id: form.getFieldValue('department_id'),
                            branch_office_id: form.getFieldValue('branch_office_id'),
                            date: form.getFieldValue('date'),
                            query: form.getFieldValue('query') || queryString.query,
                            outsourcing_id: queryString.outsourcing_id,
                            user_id: queryString.user_id,
                            type: 'confirmed',
                          }}
                        />
                    }
                  </items>
                </Tabs>
              }
            </Col>
          </Row>
        </Col>
      </Row>
    </Spin>
  );
};

export default ReservationByDatePage;
